import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import '../styles/AIJobMatches.css';

interface MatchedJob {
  id: string;
  source: string;
  title: string;
  company: string;
  location: string;
  description: string;
  salary: string;
  url: string;
  postedAt: string;
  experienceLevel: string;
  isRemote: boolean;
  matchScore: number;
  matchReasons: string[];
}

const AIJobMatches: React.FC = () => {
  const [jobs, setJobs] = useState<MatchedJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [profileCompleteness, setProfileCompleteness] = useState(0);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [setupRequired, setSetupRequired] = useState(false);
  const [minScore, setMinScore] = useState(30);
  const navigate = useNavigate();

  const fetchMatches = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/auth/jobs/ai-matches', {
        params: { limit: 30, minScore }
      });

      setJobs(response.data.jobs || []);
      setProfileCompleteness(response.data.profileCompleteness || 0);
      setSuggestions(response.data.suggestions || []);
      setSetupRequired(false);
    } catch (error: any) {
      if (error.response?.data?.setup_required) {
        setSetupRequired(true);
      } else {
        toast.error('Failed to load AI matches');
      }
    } finally {
      setLoading(false);
    }
  }, [minScore]);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  const handleFeedback = async (jobId: string, isRelevant: boolean) => {
    try {
      const job = jobs.find(j => j.id === jobId);
      await axiosInstance.post('/auth/jobs/ai-feedback', {
        jobId,
        matchScore: job?.matchScore,
        isRelevant
      });
      toast.success('Feedback recorded - thank you!');
    } catch (error) {
      console.error('Feedback error:', error);
    }
  };

  const handleSaveJob = async (job: MatchedJob) => {
    try {
      await axiosInstance.post('/auth/jobs/save', {
        adzunaJobId: job.id,
        title: job.title,
        company: job.company,
        location: job.location,
        description: job.description,
        salary: job.salary,
        url: job.url
      });
      toast.success('Job saved!');
    } catch (error) {
      toast.error('Failed to save job');
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'excellent';
    if (score >= 50) return 'good';
    if (score >= 30) return 'fair';
    return 'low';
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="ai-container">
        <div className="ai-loading">
          <div className="ai-loading-spinner" />
          <p>Finding your best job matches...</p>
          <span>Analyzing your profile and preferences</span>
        </div>
      </div>
    );
  }

  if (setupRequired) {
    return (
      <div className="ai-container">
        <div className="ai-header">
          <button className="ai-back-btn" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </button>
          <h1>AI Job Matching</h1>
        </div>

        <div className="ai-setup-required">
          <div className="ai-setup-icon">🎯</div>
          <h2>Complete Your Profile</h2>
          <p>To get personalized AI job recommendations, please complete your profile with:</p>
          <ul>
            <li>Preferred job titles</li>
            <li>Target industries</li>
            <li>Experience level</li>
            <li>Skills and certifications</li>
            <li>Preferred work location</li>
          </ul>
          <button className="ai-setup-btn" onClick={() => navigate('/profile')}>
            Complete Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-container">
      <div className="ai-header">
        <button className="ai-back-btn" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <div className="ai-header-content">
          <h1>🎯 AI Job Matching</h1>
          <p>Personalized job recommendations based on your profile</p>
        </div>
      </div>

      {/* Profile Completeness */}
      <div className="ai-profile-section">
        <div className="ai-profile-bar">
          <div className="ai-profile-info">
            <span>Profile Completeness</span>
            <span className="ai-profile-percent">{profileCompleteness}%</span>
          </div>
          <div className="ai-progress">
            <div 
              className="ai-progress-fill" 
              style={{ width: `${profileCompleteness}%` }}
            />
          </div>
        </div>

        {suggestions.length > 0 && (
          <div className="ai-suggestions">
            <h4>💡 Improve your matches:</h4>
            <ul>
              {suggestions.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
            <button onClick={() => navigate('/profile')}>Update Profile</button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="ai-filters">
        <label>
          Minimum Match Score:
          <select value={minScore} onChange={e => setMinScore(parseInt(e.target.value))}>
            <option value="10">10% and above</option>
            <option value="30">30% and above</option>
            <option value="50">50% and above</option>
            <option value="70">70% and above</option>
          </select>
        </label>
        <button onClick={fetchMatches}>Refresh Matches</button>
      </div>

      {/* Results */}
      {jobs.length === 0 ? (
        <div className="ai-empty">
          <p>No jobs match your criteria at the moment.</p>
          <p>Try lowering the minimum score or updating your profile preferences.</p>
        </div>
      ) : (
        <>
          <div className="ai-results-header">
            <span>Found {jobs.length} matches for you</span>
          </div>

          <div className="ai-jobs-list">
            {jobs.map(job => (
              <div key={job.id} className="ai-job-card">
                <div className="ai-job-score">
                  <div className={`ai-score-badge ${getScoreColor(job.matchScore)}`}>
                    {job.matchScore}%
                    <span>match</span>
                  </div>
                </div>

                <div className="ai-job-content">
                  <div className="ai-job-header">
                    <h3>{job.title}</h3>
                    <div className="ai-job-badges">
                      <span className={`ai-badge ai-badge-${job.source}`}>{job.source}</span>
                      {job.isRemote && <span className="ai-badge ai-badge-remote">🏠 Remote</span>}
                    </div>
                  </div>

                  <p className="ai-job-company">{job.company}</p>
                  <p className="ai-job-location">📍 {job.location}</p>

                  <div className="ai-match-reasons">
                    {job.matchReasons.map((reason, i) => (
                      <span key={i} className="ai-reason">✓ {reason}</span>
                    ))}
                  </div>

                  <p className="ai-job-description">
                    {job.description.substring(0, 200)}{job.description.length > 200 ? '...' : ''}
                  </p>

                  <div className="ai-job-meta">
                    <span className="ai-job-salary">💰 {job.salary}</span>
                    <span className="ai-job-posted">📅 {formatDate(job.postedAt)}</span>
                  </div>

                  <div className="ai-job-actions">
                    <a href={job.url} target="_blank" rel="noopener noreferrer" className="ai-btn ai-btn-primary">
                      View Job →
                    </a>
                    <button onClick={() => handleSaveJob(job)} className="ai-btn ai-btn-secondary">
                      📌 Save
                    </button>
                    <div className="ai-feedback">
                      <span>Was this relevant?</span>
                      <button onClick={() => handleFeedback(job.id, true)} title="Yes, relevant">👍</button>
                      <button onClick={() => handleFeedback(job.id, false)} title="Not relevant">👎</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AIJobMatches;
